import { FormikErrors } from 'formik'
import { t } from 'i18next'

import { isZero } from '@utils/validations'

import { PaymentProvider } from '@model/PaymentProviderDto'
import {
  PaymentProviderType,
  isPaymentProviderBankType,
  isPaymentProviderCardProviderType,
  isPaymentProviderCryptoType,
  isPaymentProviderPSPType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'
import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { FormatMoney } from '@hooks/useFormatNumber'

import {
  GetDepositCurrencyLimits,
  GetDepositCurrencyLimitsValue,
} from './hooks/useGetDepositCurrencyLimits'

interface StateProps {
  wallet?: WalletDto
  walletType: WalletTypeEnum
  paymentProvider?: PaymentProvider
}

export const walletDepositInitialState = (props?: StateProps): WalletDepositFormValues => {
  const { wallet, walletType, paymentProvider } = props || {}
  return {
    wallet: {
      id: wallet?.id || '',
      name: wallet?.name || '',
      currency: {
        id: wallet?.currency?.id || '',
        name: wallet?.currency?.name || '',
      },
      walletType: {
        id: wallet?.walletType?.id || 0,
        name: wallet?.walletType?.name || '',
      },
      depositAction: {
        availableToDeposit: wallet?.depositAction?.availableToDeposit || 0,
        canDeposit: wallet?.depositAction?.canDeposit || false,
      },
      balance: wallet?.balance || 0,
    },
    paymentProvider: paymentProvider || {
      id: undefined,
      name: undefined,
      description: '',
      providerCategory: { id: PaymentProviderType.None },
      currency: { id: '', name: '', minAmount: 0 },
      parameters: {
        currencies: [],
        currenciesLimits: [],
        messages: [{ title: [], list: [], top: [], bottom: [] }],
        presets: [],
        termsConditions: [],
        fields: [],
        form: [],
      },
      method_name: '',
    },
    walletType: walletType,
    fields: {},
    amount: undefined,
    comment: '',
    terms: {
      depositing: false,
      paymentOperations: false,
    },
    conversionRate: 1,
    amountSource: 'manual',
  }
}

export const validateWalletDepositForm = async (
  values: WalletDepositFormValues,
  {
    formatMoney,
    getDepositCurrencyLimits,
    isETDFirstTimeDeposit,
  }: {
    formatMoney: FormatMoney
    getDepositCurrencyLimits: GetDepositCurrencyLimits
    isETDFirstTimeDeposit?: boolean
  }
): Promise<FormikErrors<WalletDepositFormValues>> => {
  const errors: FormikErrors<WalletDepositFormValues> = {}
  const isPaymentAgentProviderType =
    values.paymentProvider.providerCategory.id === PaymentProviderType.PaymentAgent

  if (!values.wallet) {
    errors.wallet = {
      name: t('Validation.Required'),
    }
  }

  if (!values?.paymentProvider?.name) {
    errors.paymentProvider = {}
    errors.paymentProvider.name = t('Validation.Required')
  }

  const providerCategoryId = values.paymentProvider?.providerCategory?.id

  if (
    isPaymentProviderPSPType(providerCategoryId) ||
    isPaymentProviderCardProviderType(providerCategoryId) ||
    isPaymentProviderBankType(providerCategoryId) ||
    isPaymentProviderCryptoType(providerCategoryId)
  ) {
    const currencyLimits = await getDepositCurrencyLimits(values.wallet, values.paymentProvider)

    const { minAmount, maxAmount } = getErrorAmount(currencyLimits)
    const amount = values?.amount

    if (amount === undefined || amount === null) {
      errors.amount = t('Enter an amount')
    } else if (amount <= 0 || isZero(amount)) {
      errors.amount = t('Amount must be greater than zero')
    } else if (isMinAmountError(values, minAmount)) {
      errors.amount = t('Method minimum is {{amount}}', {
        amount: formatMoney(minAmount, values.paymentProvider.currency.id),
      })
    } else if (isMaxAmountError(values, maxAmount)) {
      errors.amount = t('Method maximum is {{amount}}', {
        amount: formatMoney(maxAmount, values.paymentProvider.currency.id),
      })
    } else if (isValidAmount(values) && !isETDFirstTimeDeposit) {
      if (values?.paymentProvider?.currency?.id && values?.paymentProvider?.currency?.minAmount) {
        errors.amount = t('Method minimum is {{amount}}', {
          amount: formatMoney(
            values.paymentProvider.currency.minAmount,
            values.paymentProvider.currency.id
          ),
        })
      } else {
        errors.amount = t('Method minimum is {{amount}}', {
          amount: formatMoney(1000, 'USD'),
        })
      }
    }

    if (!values.terms.paymentOperations) {
      errors.terms = {}
      errors.terms.paymentOperations = t('Validation.Required')
    }
  }

  if (values.amount && values.availableBonusCampaigns?.length) {
    const selectedBonusCampaign = values.availableBonusCampaigns.find(
      (campaign) => campaign.bonusCampaign.id === values.bonusCampaignId
    )

    if (selectedBonusCampaign) {
      const bonusDepositAmount =
        values.paymentProvider?.currency?.id === values.wallet?.currency?.id
          ? values.amount
          : values.amount && values.conversionRate
          ? Math.round(values.amount * values.conversionRate * 100) / 100
          : values.amount

      if (bonusDepositAmount < selectedBonusCampaign.minDepositAmount) {
        errors.bonusAmount = t('Wallet.Minimum deposit amount for this bonus is', {
          minAmount: formatMoney(selectedBonusCampaign.minDepositAmount, values.wallet.currency.id),
        })
      }
    }
  }

  if (isPaymentProviderPaymentAgentType(providerCategoryId)) {
    if (!values.terms.paymentOperations) {
      errors.terms = {}
      errors.terms.paymentOperations = t('Validation.Required')
    }
  }

  if (isPaymentAgentProviderType) {
    if (!values.terms.powerOfAttorney) {
      errors.terms = {}
      errors.terms.powerOfAttorney = t('Validation.Required')
    }
  }

  if (values.comment.length > 100) {
    errors.comment = t('Validation.The character limit is 100 characters')
  }

  if (
    values.paymentProvider.id &&
    isChinaUnionPayPSP(values.paymentProvider.id) &&
    !values.terms.cupResponsibility
  ) {
    errors.terms = errors.terms || {}
    errors.terms.cupResponsibility = t('Validation.Required')
  }

  return errors
}

const getErrorAmount = (
  currencyLimit?: GetDepositCurrencyLimitsValue
): GetDepositCurrencyLimitsValue => {
  const minAmount = currencyLimit?.minAmount || 0
  const maxAmount = currencyLimit?.maxAmount || 0

  return { minAmount, maxAmount }
}

const isMinAmountError = (values: WalletDepositFormValues, minAmount: number): boolean => {
  const amount = values?.amount || 0

  return amount < minAmount || isZero(amount)
}

const isMaxAmountError = (values: WalletDepositFormValues, maxAmount: number): boolean => {
  const amount = values?.amount || 0

  return amount > maxAmount
}

const isValidAmount = (values: WalletDepositFormValues): boolean => {
  const minAmount = values?.paymentProvider?.currency?.minAmount || 0
  const amount = values?.amount || 0

  return amount < minAmount
}

export const isChinaUnionPayPSP = (providerId: string): boolean =>
  [
    {
      id: '9599bfdb-825e-11eb-a2ad-06e70b382a0c',
      name: 'China UnionPay - Channel Z (SC Zotapay CNY (CUP))',
    },
    {
      id: '9017bcc6-939e-11ea-9c68-000c29df7a17',
      name: 'China UnionPay - Channel T (SC Transact365 CNY)',
    },
    {
      id: 'abfb42e9-9d79-11ed-8b0c-0a51dcb142a4',
      name: 'China Online Bank Transfer (SC Xpay CNY)',
    },
  ]
    .map((psp) => psp.id)
    .includes(providerId)
